import { groupBotsForSidebar, nestRosterByParent } from "@rakazo/core";
import { isPinnedSidebarGroupKey } from "../../lib/pinned-bots";

type SidebarChat = {
  id: string;
  name: string;
  title?: string | null;
  preview?: string | null;
  pinned: boolean;
  sectionId: string | null;
  parentBotId?: string | null;
};

type SidebarSpace<TBot extends SidebarChat, TGroup extends SidebarChat> = {
  id: string;
  name: string;
  canRename?: boolean;
  canDelete?: boolean;
  bots: readonly TBot[];
  groups: readonly TGroup[];
  botSections: readonly { id: string; name: string }[];
};

function spaceHeaderMenu(space: { canRename?: boolean; canDelete?: boolean }, hostsMenu: boolean) {
  return {
    canRenameSpace: hostsMenu && space.canRename === true,
    canDeleteSpace: hostsMenu && space.canDelete === true,
    // Every member can open a space's settings, so the menu does not depend on rename or delete.
    canOpenSpaceSettings: hostsMenu,
  };
}

export async function commitSpaceRename(input: {
  rename: () => Promise<void>;
  apply: () => void;
  refresh: () => Promise<void>;
}): Promise<void> {
  await input.rename();
  input.apply();
  await input.refresh().catch(() => undefined);
}

export function sidebarGroupsForSpaces<TBot extends SidebarChat, TGroup extends SidebarChat>(
  spaces: readonly SidebarSpace<TBot, TGroup>[],
  query: string,
  labels?: { pinned: string; unassigned: string; groups?: string },
) {
  const needle = query.toLowerCase();
  const showSpaceNames = spaces.length > 1;
  return spaces.flatMap((space) => {
    const visibleBots = space.bots.filter((bot) =>
      `${bot.name} ${bot.title ?? ""} ${bot.preview ?? ""}`.toLowerCase().includes(needle),
    );
    const visibleGroups = space.groups.filter((group) =>
      `${group.name} ${group.preview ?? ""}`.toLowerCase().includes(needle),
    );
    // Group chats get their own list under the bots unless they were pinned or put in a section.
    const sectionIds = new Set(space.botSections.map((section) => section.id));
    const placed = (group: TGroup) =>
      group.pinned || (group.sectionId !== null && sectionIds.has(group.sectionId));
    const groupChats = visibleGroups
      .filter((group) => !placed(group))
      .map((chat) => ({
        kind: "group" as const,
        chat,
        id: chat.id,
        parentBotId: null,
        pinned: chat.pinned,
        sectionId: chat.sectionId,
      }));
    const grouped = groupBotsForSidebar(
      [
        ...visibleBots.map((chat) => ({ kind: "bot" as const, chat })),
        ...visibleGroups.filter(placed).map((chat) => ({ kind: "group" as const, chat })),
      ].map((item) => ({
        ...item,
        id: item.chat.id,
        parentBotId: item.kind === "bot" ? (item.chat.parentBotId ?? null) : null,
        pinned: item.chat.pinned,
        sectionId: item.chat.sectionId,
      })),
      space.botSections,
      { labels },
    );
    // Keep destinations visible after their last bot is dragged out. Search
    // still hides empty lists, and spaces without sections keep their old layout.
    if (!needle && space.botSections.length > 0 && grouped.length > 0) {
      for (const section of space.botSections) {
        const key = `section:${section.id}`;
        if (!grouped.some((group) => group.key === key)) {
          grouped.push({ key, title: section.name, bots: [] });
        }
      }
      if (!grouped.some((group) => group.key === "unassigned")) {
        grouped.push({ key: "unassigned", title: labels?.unassigned ?? "Unassigned", bots: [] });
      }
      const order = [
        "pinned",
        ...space.botSections.map((section) => `section:${section.id}`),
        "unassigned",
      ];
      grouped.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }
    if (groupChats.length > 0) {
      grouped.push({ key: "groups", title: labels?.groups ?? "Group chats", bots: groupChats });
    }
    const sections = grouped.map((group, index) => ({
      ...group,
      sectionId: group.key.startsWith("section:") ? group.key.slice("section:".length) : null,
      key: showSpaceNames ? `space:${space.id}:${group.key}` : group.key,
      // A header holding the space menu is never blank, even for the only space.
      title: showSpaceNames
        ? group.title
          ? `${space.name} · ${group.title}`
          : space.name
        : index === 0 && !group.title
          ? space.name
          : group.title,
      showLock: showSpaceNames,
      emptySpaceId: undefined as string | undefined,
      spaceId: space.id,
      spaceName: space.name,
      ...spaceHeaderMenu(space, index === 0),
    }));
    if (sections.length > 0) return sections;
    // Keep empty spaces selectable; chat clicks are the only switch control.
    if (needle && (space.bots.length > 0 || space.groups.length > 0)) return [];
    // Saved sections stay listed under the placeholder so they can still be
    // renamed or deleted after their last chat leaves.
    const emptySections = needle
      ? []
      : space.botSections.map((section) => ({
          key: showSpaceNames ? `space:${space.id}:section:${section.id}` : `section:${section.id}`,
          title: showSpaceNames ? `${space.name} · ${section.name}` : section.name,
          bots: [] as (typeof sections)[number]["bots"],
          sectionId: section.id as string | null,
          showLock: showSpaceNames,
          emptySpaceId: undefined as string | undefined,
          spaceId: space.id,
          spaceName: space.name,
          ...spaceHeaderMenu(space, false),
        }));
    // A lone space is the selected one, so its settings stay in the account menu too.
    const lone = !showSpaceNames && !space.canRename && !space.canDelete;
    if (lone && (needle || emptySections.length > 0)) {
      return emptySections;
    }
    return [
      {
        key: showSpaceNames ? `space:${space.id}:empty` : `space:${space.id}:actions`,
        title: space.name,
        bots: [],
        sectionId: null,
        showLock: showSpaceNames,
        emptySpaceId: showSpaceNames ? space.id : undefined,
        spaceId: space.id,
        spaceName: space.name,
        ...spaceHeaderMenu(space, true),
      },
      ...emptySections,
    ];
  });
}

/** Count the unread flags the roster renders. Pass unfiltered groups for the app badge. */
export function countUnreadChats(
  sections: readonly { bots: readonly { chat: { unread?: boolean } }[] }[],
): number {
  return sections.reduce(
    (count, section) => count + section.bots.filter((item) => item.chat.unread).length,
    0,
  );
}

/** Use the same sections and nesting as navigation, without its transient search.
 * Спільні розділи й вкладеність меню без тимчасового пошукового фільтра. */
export function mobileRailBotsForSpaces<TBot extends SidebarChat, TGroup extends SidebarChat>(
  spaces: readonly SidebarSpace<TBot, TGroup>[],
  collapsedSections: ReadonlySet<string>,
  collapsedParents: ReadonlySet<string>,
) {
  return sidebarGroupsForSpaces(spaces, "").flatMap((group) => {
    if (group.title && collapsedSections.has(group.key)) return [];
    // Pinned navigation tiles have no parent-expansion control.
    // Закріплені плитки меню не мають розгортання підлеглих ботів.
    const parents = isPinnedSidebarGroupKey(group.key) ? new Set<string>() : collapsedParents;
    return nestRosterByParent(group.bots, parents).flatMap(({ item }) =>
      item.kind === "bot" ? [{ ...item.chat, spaceId: group.spaceId }] : [],
    );
  });
}
